import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import {
    capturePropertyFilter,
    propertiesActions,
    type PropertyFilterSnapshot,
    propertyKey,
    type SelectedProperty,
    selectSelectedProperties,
} from "features/properties/slice";

import { type PropertyGroup } from "./usePropertyFiltering";

export type { SelectedProperty };

export interface UsePropertySelectionResult {
    selectedProperties: Record<string, SelectedProperty>;
    isPropertySelected: (groupName: string, propertyName: string, propertyValue: string) => boolean;
    getGroupStats: (groupName: string) => {
        total: number;
        selected: number;
        isEmpty: boolean;
        isFullySelected: boolean;
        isPartiallySelected: boolean;
    };
    togglePropertySelection: (groupName: string, propertyName: string, propertyValue: string) => void;
    toggleGroupSelection: (groupName: string) => void;
}

/**
 * Reads and toggles the Properties window checkbox selection, which lives in the
 * `properties` slice so it survives the window closing.
 *
 * @param options.onSelectionChange Called with the selection after a toggle is applied and
 *   the whole filter from before it. The viewer side effect is wired here rather than to an
 *   effect on the selection so it fires only on a user toggle, not when
 *   `resetView`/`setViewpoint` clear the slice. The `before` value is what that side
 *   effect's undo has to restore.
 */
export function usePropertySelection(
    groupedProperties: PropertyGroup[],
    options?: {
        onSelectionChange?: (next: Record<string, SelectedProperty>, before: PropertyFilterSnapshot) => void;
    },
): UsePropertySelectionResult {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const selectedProperties = useAppSelector(selectSelectedProperties);

    const getGroup = (groupName: string) => groupedProperties.find((g) => g.name === groupName);

    const isPropertySelected = (groupName: string, propertyName: string, propertyValue: string) =>
        propertyKey(groupName, propertyName, propertyValue) in selectedProperties;

    const getGroupStats = (groupName: string) => {
        const groupProps = getGroup(groupName)?.properties ?? [];
        const selectedCount = groupProps.filter(([propName, propValue]) =>
            isPropertySelected(groupName, propName, propValue),
        ).length;

        return {
            total: groupProps.length,
            selected: selectedCount,
            isEmpty: selectedCount === 0,
            isFullySelected: selectedCount > 0 && selectedCount === groupProps.length,
            isPartiallySelected: selectedCount > 0 && selectedCount < groupProps.length,
        };
    };

    /** Read either side of the toggle off the store, so neither depends on render timing. */
    const applyToggle = (action: Parameters<typeof dispatch>[0]) => {
        const before = capturePropertyFilter(store.getState());
        dispatch(action);
        options?.onSelectionChange?.(selectSelectedProperties(store.getState()), before);
    };

    const togglePropertySelection = (groupName: string, propertyName: string, propertyValue: string) => {
        applyToggle(
            propertiesActions.togglePropertySelection({
                groupName,
                propertyName,
                propertyValue,
                isGeneralGroup: getGroup(groupName)?.isGeneral ?? false,
            }),
        );
    };

    const toggleGroupSelection = (groupName: string) => {
        const group = getGroup(groupName);

        if (!group) {
            return;
        }

        applyToggle(
            propertiesActions.toggleGroupSelection({
                groupName,
                properties: group.properties,
                isGeneralGroup: group.isGeneral ?? false,
            }),
        );
    };

    return {
        selectedProperties,
        isPropertySelected,
        getGroupStats,
        togglePropertySelection,
        toggleGroupSelection,
    };
}
